// ======================= React & Style ==========================
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setMentionIdAction,
  setPreparedCanvasAction,
} from '../../../redux/actions/collaboration';
// ======================== Apollo ================================
import { useMutation, useQuery } from '@apollo/client';
import preloadUrlQuery from '../apollo/preloadUrlQuery';
import createMessageMutation from './apollo/createMessageMutation';
// ======================== Component =============================
import TextInputArea from '../../TextInputArea';
import { convertToRaw, EditorState } from 'draft-js';
// ======================== Utils =================================
import CanvasControl from './CanvasControl';
import { putImageToS3 } from '../../../utils/fileUpload';
import getRedirectBaseUrl from '../../../utils/getRedirectBaseUrl';
import {
  editorDecorator,
  getMentionedUsers,
  trimEditorContent,
} from '../utils';
import {
  AnnotationHeightPadding,
  AnnotationWidthPadding,
  drawCircle,
  findxy,
  MinMessageInputWidth,
  penColors,
  setLineColor,
} from './utils';
import { Dialog } from '@mui/material';
// ======================= Styled-Component ======================
import * as ScreenshotUI from './ScreenshotUI';
import { useAppSelector } from '../../../redux/hooks';
import { workspacePropertySelector } from '../../../redux/selectors/workspace';
import {
  IPreloadUrlQueryQuery,
  IPreloadUrlQueryQueryVariables,
  IScreenshotMessageCreateMutation,
  IScreenshotMessageCreateMutationVariables,
  UploadKind,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import { HEADER_Z_INDEX, SIDEBAR_Z_INDEX } from '../../../styles/zIndexes';
// ======================= TypeScript ============================

export default () => {
  const dispatch = useDispatch();

  const workspaceId = useAppSelector((state) =>
    workspacePropertySelector(state, 'id'),
  );
  const originCanvas = useSelector(
    (state: any) => state.collaboration.preparedCanvas,
  );
  const activeTag = useSelector((state: any) => state.collaboration.activeTag);
  const userList = useSelector((state: any) => state.collaboration.userList);
  const mainChannelId = useSelector(
    (state: any) => state.collaboration.mainChannelId,
  );
  const currentChannelId = useSelector(
    (state: any) => state.collaboration.currentChannelId,
  );

  const isMainChannel = mainChannelId === currentChannelId;

  const setOriginCanvas = useCallback(
    (canvas: any) => {
      dispatch(setPreparedCanvasAction(canvas));
    },
    [dispatch],
  );

  const setMentionId = useCallback(
    (mentionId: any) => {
      dispatch(setMentionIdAction(mentionId));
    },
    [dispatch],
  );

  // Fetch the preUpload data by workspace id
  const { loading: isLoading, data } = useQuery<
    IPreloadUrlQueryQuery,
    IPreloadUrlQueryQueryVariables
  >(preloadUrlQuery, {
    variables: {
      id: workspaceId,
      kind: UploadKind.Annotation,
    },
  });

  // set marker default to circle on canvas
  const [markerName, updateMarker] = useState('circle');
  // exist mentioned list
  const [mentionedUsers, setMentionedUsers] = useState([]);

  // color pattern for canvas
  const [color, setColor] = useState(penColors[0]);

  // confirm Modal to close screenshot
  const [cancelConfirm, setCancelConfirm] = useState(false);
  // initial draftjs state
  const [editorState, setEditorState] = React.useState(
    EditorState.createEmpty(editorDecorator({ clickMention: setMentionId })),
  );

  // disable refresh button while rendering new canvas
  const [isRefreshDisabled, setRefreshDisabled] = useState(false);

  // Draftjs editor current state
  const editorContent = editorState.getCurrentContent();
  const plainMessage = editorContent.getPlainText().trim();
  const [selectorPos, setSelectorPos] = useState(0);

  // initialise canvas
  const newCanvas = document.createElement('canvas');
  const context: any = newCanvas.getContext('2d');
  newCanvas.width = originCanvas.width;
  newCanvas.height = originCanvas.height;
  context.drawImage(originCanvas, 0, 0);
  const [canvas, setCanvas] = useState(context.canvas);

  // Remove header z-index so it is behind screenshot
  useEffect(() => {
    const header = document.getElementsByClassName('header')[0] as any;
    const sidebar = document.getElementById('sidebar-container') as any;

    sidebar.style.zIndex = 0;
    header.style.zIndex = 0;
    return () => {
      header.style.zIndex = HEADER_Z_INDEX;
      sidebar.style.zIndex = SIDEBAR_Z_INDEX;
    };
  }, []);

  const confirmMutation = (postMutation: any, preUpload: any) => {
    if (activeTag || plainMessage.length > 4000) {
      return;
    }

    // generate formatted text
    const trimmedEditorState = trimEditorContent(editorState);
    const trimmedContent = trimmedEditorState.getCurrentContent();
    const rawContent = convertToRaw(trimmedContent);
    const { blocks, ...restContent } = rawContent || [];
    const startIndex: number = blocks.findIndex((block) => !!block.text);
    const endIndex: number =
      blocks.length - 1 - blocks.reverse().findIndex((block) => !!block.text);
    const slicedBlocks = blocks.reverse().slice(startIndex, endIndex + 1);
    input.formatted = JSON.stringify({ blocks: slicedBlocks, ...restContent });

    setOriginCanvas(undefined);

    const imageData = canvas?.toDataURL('image/png');

    // generate mention list
    const newMentionList = getMentionedUsers(
      userList,
      mentionedUsers,
      plainMessage,
    );

    if (newMentionList.length > 0) {
      input.metadata = {
        mentions: newMentionList,
        landingUrl: getRedirectBaseUrl(),
      };
    }
    putImageToS3(imageData, preUpload).then((_: any) => {
      postMutation();
      setEditorState(EditorState.createEmpty(editorDecorator({})));
      setMentionedUsers([]);
    });
  };

  const refreshCanvas = async () => {
    setRefreshDisabled(true);
    setCanvas(context.canvas);
  };

  // mouse events for canvas drawing
  const mouseUpHandler = useCallback(
    (e: any) => {
      const ctx = canvas.getContext('2d');
      if (markerName === 'pencil' && ctx !== null) {
        findxy('up', e, canvas, ctx);
      }
      if (markerName === 'circle' && ctx !== null) {
        drawCircle(e, canvas, ctx);
      }
    },
    [markerName, canvas],
  );

  const mouseMoveHandler = useCallback(
    (e: any) => {
      const ctx = canvas.getContext('2d');
      if (markerName === 'pencil' && ctx !== null) {
        findxy('move', e, canvas, ctx);
      }
    },
    [markerName, canvas],
  );

  const mouseDownHandler = useCallback(
    (e: any) => {
      const ctx = canvas.getContext('2d');
      if (markerName === 'pencil' && ctx !== null) {
        findxy('down', e, canvas, ctx);
      }
    },
    [markerName, canvas],
  );

  const mouseOutHandler = useCallback(
    (e: any) => {
      const ctx = canvas.getContext('2d');
      if (markerName === 'pencil' && ctx !== null) {
        findxy('out', e, canvas, ctx);
      }
    },
    [markerName, canvas],
  );
  const keydownEvent = (event: any) => {
    if (event.key === 'Escape') {
      if (plainMessage.length !== 0) {
        setCancelConfirm(true);
      } else {
        setOriginCanvas(undefined);
      }
    }
  };

  useEffect(() => {
    if (!isLoading) {
      const modalDiv = document.getElementById('ScreenshotModal');
      modalDiv?.focus();
    }
  }, [isLoading]);

  // Canvas render and update effect
  useEffect(() => {
    const updateCanvas = () => {
      const currCanvas = document.querySelector('.CanvasImage > canvas');
      // let chartDiv: any;
      let isUpdate = false;
      if (currCanvas) {
        isUpdate = true;
      }

      // check for ignonre line icon for canvas.
      if (canvas) {
        setLineColor(color);
        const canvasParent = document.getElementById('CanvasImage');
        if (isUpdate && currCanvas) {
          currCanvas.replaceWith(canvas);
        } else if (canvasParent) {
          canvasParent.append(canvas);
        }
      }
    };
    if (isLoading) {
      return;
    }
    if (canvas) {
      canvas.addEventListener('mouseup', mouseUpHandler);
      canvas.addEventListener('mousemove', mouseMoveHandler);
      canvas.addEventListener('mousedown', mouseDownHandler);
      canvas.addEventListener('mouseout', mouseOutHandler);
    }

    updateCanvas();
    setRefreshDisabled(false);
    return () => {
      if (canvas) {
        canvas.removeEventListener('mouseup', mouseUpHandler);
        canvas.removeEventListener('mousemove', mouseMoveHandler);
        canvas.removeEventListener('mousedown', mouseDownHandler);
        canvas.removeEventListener('mouseout', mouseOutHandler);
      }
    };
  }, [
    color, // prettier ignore
    markerName,
    canvas,
    originCanvas,
    isLoading,
    mouseUpHandler,
    mouseMoveHandler,
    mouseDownHandler,
    mouseOutHandler,
  ]);

  // message submit input
  const input: any = {
    channelId: currentChannelId,
    text: plainMessage,
    formatted: '',
    parent: '',
    metadata: {
      landingUrl: getRedirectBaseUrl(),
    },
  };

  // Create message mutation
  const [createPostMutation] = useMutation<
    IScreenshotMessageCreateMutation,
    IScreenshotMessageCreateMutationVariables
  >(createMessageMutation);

  const submitMutation = () => {
    assertNotNil(data?.preUpload);
    input.key = data.preUpload.objectKey;
    createPostMutation({ variables: { input } });
  };
  if (isLoading) {
    return <>loading...</>;
  }

  let contentWidth = 0;
  let contentHeight = 0;
  // Calculate width of image container, if width < 200, set it to 200 for display message input bar
  if (canvas.width > window.innerWidth - AnnotationWidthPadding) {
    contentWidth = window.innerWidth - AnnotationWidthPadding;
  } else {
    contentWidth =
      canvas.width > MinMessageInputWidth ? canvas.width : MinMessageInputWidth;
  }

  // Caculate height of image container
  if (canvas.height > window.innerHeight - AnnotationHeightPadding) {
    contentHeight = window.innerHeight - AnnotationHeightPadding;
  } else {
    contentHeight = canvas.height;
  }

  assertNotNil(data);
  return (
    <ScreenshotUI.BackDrop
      id="ScreenshotModal"
      tabIndex={1}
      onKeyDown={keydownEvent}
    >
      <ScreenshotUI.Container
        height={contentHeight}
        width={contentWidth}
        id="ScreenshotContainer"
      >
        <CanvasControl
          setColor={setColor}
          penColors={penColors}
          color={color}
          refreshCanvas={refreshCanvas}
          isRefreshDisabled={isRefreshDisabled}
          markerName={markerName}
          updateMarker={updateMarker}
        />
        <ScreenshotUI.CanvasRight>
          <ScreenshotUI.CanvasImage className="CanvasImage" id="CanvasImage" />
          <ScreenshotUI.InputContainer width={contentWidth}>
            <>
              <ScreenshotUI.MessageTitle>Message</ScreenshotUI.MessageTitle>
              <TextInputArea
                activeTag={activeTag}
                height={'72px'}
                editorState={editorState}
                setEditorState={setEditorState}
                postMutation={submitMutation}
                preUpload={data.preUpload}
                confirmMutation={confirmMutation}
                userList={userList}
                isMainChannel={isMainChannel}
                mentionedUsers={mentionedUsers}
                setMentionedUsers={setMentionedUsers}
                selectorPos={selectorPos}
                setSelectorPos={setSelectorPos}
              />

              <ScreenshotUI.ConfirmButtons>
                <ScreenshotUI.ButtonIcon
                  name="cancel"
                  defaultChecked={false}
                  onClick={() => setOriginCanvas(undefined)}
                />

                <ScreenshotUI.ButtonIcon
                  name="send"
                  onClick={() =>
                    confirmMutation(submitMutation, data.preUpload)
                  }
                  defaultChecked={
                    activeTag || plainMessage.length > 4000 ? false : true
                  }
                />
              </ScreenshotUI.ConfirmButtons>
            </>
          </ScreenshotUI.InputContainer>
        </ScreenshotUI.CanvasRight>
      </ScreenshotUI.Container>

      <Dialog open={cancelConfirm}>
        <ScreenshotUI.CancelDialogContainer>
          <ScreenshotUI.CancelDialogHeader>
            Quit Editing
          </ScreenshotUI.CancelDialogHeader>
          <ScreenshotUI.CancelDialogSplitter />
          <ScreenshotUI.CancelDialogContent>
            Do you want to cancel this editing? The screenshot and message will
            not be saved.
          </ScreenshotUI.CancelDialogContent>
          <ScreenshotUI.CancelButtonGroup>
            <ScreenshotUI.DialogCancelButton
              onClick={() => setCancelConfirm(false)}
            >
              Cancel
            </ScreenshotUI.DialogCancelButton>
            <ScreenshotUI.DialogConfirmButton
              onClick={() => setOriginCanvas(undefined)}
            >
              Confirm
            </ScreenshotUI.DialogConfirmButton>
          </ScreenshotUI.CancelButtonGroup>
        </ScreenshotUI.CancelDialogContainer>
      </Dialog>
    </ScreenshotUI.BackDrop>
  );
};
